import React from 'react';
import { GetServerSideProps } from 'next';

import AppContainer from '~/containers/AppContainer/AppContainer';
import { getTradeWineInfoFromSlug } from '~/networking/api/cms/requests/getTradeWineInfoFromSlug';
import Product from '~/components/views/Product';
import type { CMSTradeCountry } from '~/networking/api/cms/responses/CMSRegion';
import { getTradeCountryFromSlug } from '~/networking/api/cms/requests/getTradeCountryFromSlug';
import { RegionsDirectory } from '~/components/views/Directory/RegionsDirectory';
import HeaderMetaTags from '~/components/modules/Header/HeaderMetaTags';
import type { CMSTradeVintage } from '~/networking/api/cms/responses/CMSWineVintage';
import { useRouter } from 'next/router';

interface RegionProps {
    type: 'wine'; // discriminator
    wine: CMSTradeVintage;
    slug: string;
}
interface CountryProps {
    type: 'country'; // discriminator
    country: CMSTradeCountry;
}
type IndexProps = RegionProps | CountryProps;

const IndexPage: React.FC<IndexProps> = (props) => {
    const router = useRouter();
    if (props.type === 'wine') {
        const { wine } = props;
        return (
            <AppContainer>
                <HeaderMetaTags
                    url={`${process.env.NEXT_PUBLIC_HOST_ENV}${router.asPath}`}
                    title={`${wine.wineInfo.displayName} | Vinovest`}
                    image={`${wine.bottleImage?.url}`}
                    description={`Start or Expand your Wine Collection with ${wine.wineInfo.displayName} on Vinovest`}
                    canonical
                />
                <Product lwin11={wine} />
            </AppContainer>
        );
    }

    const { country } = props;

    return (
        <AppContainer>
            <HeaderMetaTags
                url={`${process.env.NEXT_PUBLIC_HOST_ENV}${router.asPath}`}
                title={`Top Wine Producing Regions in the ${country.name} - Wine Investing | Vinovest`}
                description={`Browse through Vinovest's top wine investments in the ${country.name} to help inform you of your next investments on Vinovest.`}
                canonical
            />
            <RegionsDirectory country={country} />
        </AppContainer>
    );
};

export default IndexPage;

export const getServerSideProps: GetServerSideProps = async ({ params }) => {
    const slug: string = params!.slug as string;
    let ret: IndexProps;

    if (slug.match(/^[0-9]{4}/)) {
        // is a wine page 2004-fuckin-catalina-wine-mixer-1234567
        const res = await getTradeWineInfoFromSlug(slug);
        if (!res.data || res.status !== 200) {
            return {
                notFound: true
            };
        }

        ret = { type: 'wine', wine: res.data, slug };
    } else {
        // is a country page showing regions
        const res = await getTradeCountryFromSlug(slug);
        if (!res.data || res.status !== 200) {
            return {
                notFound: true
            };
        }

        ret = { type: 'country', country: res.data };
    }

    return { props: ret };
};
